import type { ApiContract, ApiRequestOptions } from '../common/contracts';
import type { z } from 'zod';
import { AppError, ValidationError, ExternalServiceError } from '../common/errors';
import { logger as defaultLogger } from '../common/logger';

const log = defaultLogger.child({ module: 'apiClient' });

const PATH_PARAM_PATTERN = /:([A-Za-z_][A-Za-z0-9_]*)/g;

/**
 * Type-safe HTTP client that sends requests according to an API contract and
 * validates the response against the contract's Zod schema at runtime.
 *
 * Callers are responsible for catching and reporting errors to errorTracker
 * when appropriate — this function only throws, it does not report.
 *
 * @returns Parsed and validated response data whose type is derived from the contract.
 * @throws {AppError} On HTTP 4xx/5xx responses (statusCode mirrors the server's).
 * @throws {ValidationError} When the response body fails schema validation.
 * @throws {ExternalServiceError} On network failures.
 */
export async function apiClient<T extends ApiContract>(
  contract: T,
  options?: ApiRequestOptions<T>,
): Promise<z.infer<T['response']>> {
  // ApiRequestOptions<T> is a conditional intersection type that TypeScript
  // cannot resolve inside a generic body. The type safety is enforced at the
  // call site; here we destructure into known shapes.
  const { params, query, body, headers } = (options ?? {}) as {
    params?: Record<string, unknown>;
    query?: Record<string, unknown>;
    body?: unknown;
    headers?: Readonly<Record<string, string>>;
  };

  if (contract.params && params !== undefined) {
    const pResult = contract.params.safeParse(params);
    if (!pResult.success) {
      throw new ValidationError('Request path params validation failed', {
        cause: pResult.error,
      });
    }
  }

  if (contract.query && query !== undefined) {
    const qResult = contract.query.safeParse(query);
    if (!qResult.success) {
      throw new ValidationError('Request query validation failed', {
        cause: qResult.error,
      });
    }
  }

  if (contract.body && body !== undefined) {
    const bResult = contract.body.safeParse(body);
    if (!bResult.success) {
      throw new ValidationError('Request body validation failed', {
        cause: bResult.error,
      });
    }
  }

  const url = buildUrl(contract.path, params, query);
  const init = buildFetchInit(contract.method, body, headers);

  log.debug('Request started', { method: contract.method, path: contract.path });

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    log.error('Network request failed', {
      method: contract.method,
      path: contract.path,
      error: err instanceof Error ? err.message : String(err),
    });
    throw new ExternalServiceError('Network request failed', { cause: err });
  }

  if (!response.ok) {
    const errorBody = await safeReadBody(response);
    log.warn('HTTP error response', {
      method: contract.method,
      path: contract.path,
      status: response.status,
    });
    throw new AppError(errorBody?.error ?? `HTTP ${response.status}`, {
      // Prefer the server's machine-readable code (VALIDATION_ERROR, CONFLICT,
      // …) so callers can branch on `err.code`; fall back to a generic
      // HTTP_ERROR when the body carries no code (e.g. a non-contract endpoint).
      code: errorBody?.code ?? 'HTTP_ERROR',
      statusCode: response.status,
    });
  }

  const json: unknown = await response.json();

  const result = contract.response.safeParse(json);
  if (!result.success) {
    log.error('Response validation failed', {
      path: contract.path,
    });
    throw new ValidationError('Response validation failed', {
      cause: result.error,
    });
  }

  // safeParse on a generic ZodType returns unknown; the contract guarantees
  // the data matches z.infer<T['response']> after successful validation.
  return result.data as z.infer<T['response']>;
}

function buildUrl(
  path: string,
  params: Record<string, unknown> | undefined,
  query: Record<string, unknown> | undefined,
): string {
  const resolvedPath = substitutePathParams(path, params);

  if (!query) return resolvedPath;

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value));
    }
  }

  const qs = searchParams.toString();
  return qs ? `${resolvedPath}?${qs}` : resolvedPath;
}

function substitutePathParams(
  path: string,
  params: Record<string, unknown> | undefined,
): string {
  if (!path.includes(':')) return path;

  return path.replace(PATH_PARAM_PATTERN, (_match, name: string) => {
    const value = params?.[name];
    if (value === undefined || value === null || value === '') {
      throw new ValidationError(
        `Missing value for path parameter :${name} in ${path}`,
      );
    }
    return encodeURIComponent(String(value));
  });
}

function buildFetchInit(
  method: string,
  body: unknown,
  headers: Readonly<Record<string, string>> | undefined,
): RequestInit {
  const init: RequestInit = { method };

  const mergedHeaders: Record<string, string> = {};
  if (headers) {
    Object.assign(mergedHeaders, headers);
  }

  if (body !== undefined && body !== null) {
    mergedHeaders['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  if (Object.keys(mergedHeaders).length > 0) {
    init.headers = mergedHeaders;
  }

  return init;
}

/**
 * Reads the error message and machine-readable code off a 4xx/5xx response
 * body. The server-side convention is `{ error: string, code: string }`
 * (produced by `handleApiError` in `apps/fullstack/src/lib/api-error-handler.ts`);
 * `error` is the human-readable message, `code` is the stable identifier
 * callers branch on. Either field is read independently — a body missing
 * `error` falls back to the bare status code, a body missing `code` falls
 * back to `HTTP_ERROR` at the throw site.
 */
async function safeReadBody(
  response: Response,
): Promise<{ error?: string; code?: string } | null> {
  try {
    const json: unknown = await response.json();
    if (typeof json === 'object' && json !== null) {
      const body = json as { error?: unknown; code?: unknown };
      const result: { error?: string; code?: string } = {};
      if (typeof body.error === 'string') result.error = body.error;
      if (typeof body.code === 'string') result.code = body.code;
      return result;
    }
    return {};
  } catch {
    return null;
  }
}
